import {appendFieldError, createDropdown, createInputField, disableFormControls} from '../forms/formFields';
import {cloneValue} from '../forms/formState';
import {createRemoveIcon} from '../forms/repeatableFields';
import {createEmbeddingModelFields, managedEmbeddingModelValue} from './embeddingModelFields';

const providers = ['Pinecone', 'Qdrant', 'Weaviate'];
const managedUnavailableMessage = 'SimWorkflow is unavailable for this organization. Choose an external vector store.';
const configurationFields = {
    Pinecone: ['credentials', 'indexName', 'namespace', 'contentFieldName', 'distanceMetadataFieldName'],
    Qdrant: ['credentials', 'host', 'port', 'collectionName'],
    Weaviate: ['credentials', 'host', 'objectClass', 'contentFieldName', 'metaFieldPrefix', 'consistencyLevel']
};
const requiredConfigurationFields = {
    Pinecone: ['credentials', 'indexName'],
    Qdrant: ['credentials', 'host', 'port', 'collectionName'],
    Weaviate: ['credentials', 'host', 'objectClass', 'contentFieldName', 'consistencyLevel']
};
const fieldLabels = {
    credentials: 'credentials', indexName: 'vectorIndexName', namespace: 'vectorNamespace',
    contentFieldName: 'vectorContentFieldName', distanceMetadataFieldName: 'vectorDistanceFieldName',
    host: 'vectorHost', port: 'vectorPort', collectionName: 'vectorCollectionName',
    objectClass: 'vectorObjectClass', metaFieldPrefix: 'vectorMetaFieldPrefix',
    consistencyLevel: 'vectorConsistencyLevel'
};

const createVectorStoreResourceFields = ({value = {}, errors = {}, mxResources, onChange,
                                         managedVectorAllowed = false,
                                         onRender = () => {}}) => {
    const element = document.createElement('div');
    element.className = 'ui form vector-store-resource-fields';
    let current = cloneValue(value || {});
    const managedUnavailable = current.vectorStore?.provider === 'SimWorkflow' && !managedVectorAllowed;
    if (current.vectorStore?.provider === 'SimWorkflow' && managedVectorAllowed) {
        current.vectorStore = {provider: 'SimWorkflow'};
        current.embeddingModel = managedEmbeddingModelValue(current.embeddingModel);
    }
    const emit = patch => {
        current = {...current, ...patch};
        onChange(cloneValue(current));
    };

    const storeHeader = document.createElement('h4');
    storeHeader.className = 'ui dividing header';
    storeHeader.textContent = mxResources.get('vectorStoreSection');
    element.appendChild(storeHeader);

    const providerField = createDropdown({
        label: mxResources.get('vectorStoreProvider'), id: 'vectorStoreProvider', name: 'vectorStore.provider',
        className: 'required', searchable: true, value: current.vectorStore?.provider || '',
        error: typeof errors?.vectorStore === 'string' ? errors.vectorStore : errors?.vectorStore?.provider,
        help: mxResources.get('vectorStoreProviderHelp'),
        options: [{value: '', name: mxResources.get('vectorStoreProviderSelect')},
            ...providers.map(provider => ({value: provider, name: provider})),
            ...(managedVectorAllowed ? [{value: 'SimWorkflow', name: 'SimWorkflow'}]
                : managedUnavailable ? [{value: 'SimWorkflow', name: 'SimWorkflow (unavailable)'}] : [])]
    });
    if (managedUnavailable) {
        providerField.querySelector('select').querySelectorAll('option')
            .forEach(option => { if (option.value === 'SimWorkflow') option.disabled = true; });
        appendFieldError(providerField, managedUnavailableMessage, {className: 'managed-vector-unavailable'});
    }
    element.appendChild(providerField);

    const providerGroups = new Map();
    Object.entries(configurationFields).forEach(([provider, fields]) => {
        const group = document.createElement('div');
        group.dataset.provider = provider;
        fields.forEach(fieldName => {
            const required = requiredConfigurationFields[provider].includes(fieldName);
            const existing = current.vectorStore?.provider === provider ? current.vectorStore[fieldName] : '';
            const common = {
                label: mxResources.get(fieldLabels[fieldName]), id: `vector${provider}${fieldName}`,
                name: `vectorStore.${fieldName}`, value: existing ?? '',
                error: errors?.vectorStore?.[fieldName], className: required ? 'required' : ''
            };
            const field = fieldName === 'credentials' ? createDropdown({
                ...common, searchable: true, clearable: true,
                options: [{value: '', name: mxResources.get('credentialsSelect')},
                    ...(existing ? [{value: existing, name: existing}] : [])]
            }) : fieldName === 'consistencyLevel' ? createDropdown({
                ...common, options: [{value: '', name: mxResources.get('vectorConsistencyLevelSelect')},
                    ...['One', 'Quorum', 'All'].map(level => ({value: level, name: level}))]
            }) : createInputField({
                ...common, type: fieldName === 'port' ? 'number' : 'text',
                min: fieldName === 'port' ? 1 : undefined
            });
            const control = field.querySelector('select') || field.querySelector('input');
            if (fieldName === 'credentials') {
                control.className += ' credentials';
            }
            control.addEventListener('change', event => {
                if (current.vectorStore?.provider === provider) {
                    emit({vectorStore: {...current.vectorStore,
                        [fieldName]: fieldName === 'port' && event.target.value !== ''
                            ? Number(event.target.value) : event.target.value}});
                }
            });
            group.appendChild(field);
        });
        group.hidden = current.vectorStore?.provider !== provider;
        providerGroups.set(provider, group);
        element.appendChild(group);
    });
    providerField.querySelector('select').addEventListener('change', event => {
        const provider = event.target.value;
        if (provider === 'SimWorkflow' && !managedVectorAllowed) {
            return;
        }
        providerField.querySelectorAll('div').forEach(child => {
            if (child.className?.includes('managed-vector-unavailable')) child.remove();
        });
        emit({vectorStore: provider === 'Weaviate'
            ? {provider, metaFieldPrefix: ''} : {provider},
        embeddingModel: embedding.setManaged(provider === 'SimWorkflow')});
        providerGroups.forEach((group, name) => {
            group.querySelectorAll('[name^="vectorStore."]').forEach(control => {
                if (control.tagName === 'SELECT' && typeof $ === 'function') {
                    $(control).dropdown('clear');
                } else {
                    control.value = '';
                }
            });
            group.hidden = name !== provider;
        });
    });

    const embeddingHeader = document.createElement('h4');
    embeddingHeader.className = 'ui dividing header';
    embeddingHeader.textContent = mxResources.get('vectorEmbeddingSection');
    element.appendChild(embeddingHeader);
    const embedding = createEmbeddingModelFields({
        model: current.embeddingModel,
        errors: typeof errors?.embeddingModel === 'string'
            ? {provider: errors.embeddingModel} : errors?.embeddingModel,
        mxResources,
        managed: current.vectorStore?.provider === 'SimWorkflow',
        onRender,
        onChange: embeddingModel => emit({embeddingModel})
    });
    element.appendChild(embedding.element);
    return {element, getValue: () => cloneValue(current)};
};

const createVectorStoreResourcesForm = ({resources = {}, errors = {}, drafts = [], editable = true,
                                         managedVectorAllowed = false,
                                         mxResources, onChange = () => {}, onDraftAdded = () => {},
                                         onDraftRemoved = () => {}, onRender = () => {}}) => {
    let current = cloneValue(resources);
    current.vectorStores = current.vectorStores || {};
    const element = document.createElement('div');
    element.id = 'workflowVectorStoresEditor';
    element.className = 'ui form';
    if (typeof errors?.vectorStores === 'string') {
        const resourceError = document.createElement('div');
        resourceError.className = 'ui basic error large pointing prompt label vector-stores-error';
        resourceError.textContent = errors.vectorStores;
        element.appendChild(resourceError);
    }

    const transportResources = () => {
        const resources = cloneValue(current);
        Object.values(resources.vectorStores).forEach(resource => {
            if (resource?.vectorStore?.provider === '') {
                resource.vectorStore = null;
            }
            if (resource?.embeddingModel?.provider === '' && !resource.embeddingModel.credentials) {
                resource.embeddingModel = null;
            }
        });
        return resources;
    };
    const emit = () => onChange(transportResources());
    const nameEntries = new Map();
    let addButton;
    const appendResource = (name, value, draft = null) => {
        const section = document.createElement('div');
        section.className = 'ui segment';
        const header = document.createElement('div');
        header.className = 'ui small clearing header removable-section-header';
        if (editable) {
            const remove = createRemoveIcon(mxResources.get('vectorResourceRemove'));
            const removeResource = () => {
                if (draft) {
                    const index = drafts.indexOf(draft);
                    if (index !== -1) {
                        drafts.splice(index, 1);
                        onDraftRemoved();
                    }
                } else {
                    delete current.vectorStores[name];
                }
                section.remove();
                nameEntries.delete(section);
                if (!draft) {
                    emit();
                }
            };
            remove.addEventListener('mousedown', event => event.preventDefault?.());
            remove.addEventListener('click', removeResource);
            remove.addEventListener('keydown', event => {
                if (event.key === 'Enter' || event.key === ' ') {
                    event.preventDefault?.();
                    removeResource();
                }
            });
            header.appendChild(remove);
        }
        const label = document.createElement('span');
        label.textContent = mxResources.get('vectorStoreSection');
        header.appendChild(label);
        section.appendChild(header);
        if (errors?.vectorStores?.[name]?.message) {
            appendFieldError(section, errors.vectorStores[name].message);
        }
        if (editable) {
            const resourceNameField = createInputField({
                label: mxResources.get('vectorResourceName'), name: 'vectorStoreName',
                type: 'text', className: 'required', value: draft?.name ?? name,
                error: errors?.vectorStores?.[name]?.name
            });
            const resourceNameInput = resourceNameField.querySelector('input');
            const showRenameError = message => {
                [...resourceNameField.children].filter(child =>
                    child.className?.includes('pointing prompt label')).forEach(child => child.remove());
                resourceNameField.className = resourceNameField.className.replace(/\berror\b/g, '').trim();
                if (message) {
                    appendFieldError(resourceNameField, message);
                }
            };
            const rename = () => {
                const nextName = resourceNameInput.value;
                if (!nextName || !nextName.trim()) {
                    showRenameError(mxResources.get('vectorResourceNameRequired'));
                    return;
                }
                if (nextName === '__proto__') {
                    showRenameError(mxResources.get('vectorResourceNameInvalid'));
                    return;
                }
                if (nextName !== name && Object.hasOwn(current.vectorStores, nextName)) {
                    showRenameError(mxResources.get('vectorResourceNameDuplicate'));
                    return;
                }
                showRenameError(null);
                if (nextName !== name) {
                    if (draft) {
                        const index = drafts.indexOf(draft);
                        if (index === -1) {
                            return;
                        }
                        drafts.splice(index, 1);
                        onDraftRemoved(true);
                        current.vectorStores[nextName] = draft.value;
                        draft = null;
                    } else {
                        current.vectorStores = Object.fromEntries(Object.entries(current.vectorStores).map(
                            ([key, resource]) => [key === name ? nextName : key, resource]));
                    }
                    name = nextName;
                    emit();
                }
            };
            resourceNameInput.addEventListener('input', () => {
                if (draft) {
                    draft.name = resourceNameInput.value;
                }
                showRenameError(null);
            });
            resourceNameInput.addEventListener('change', rename);
            nameEntries.set(section, {input: resourceNameInput, getName: () => name,
                showError: showRenameError, rename});
            section.appendChild(resourceNameField);
        } else {
            section.appendChild(createInputField({
                label: mxResources.get('vectorResourceName'), name: 'vectorStoreName',
                type: 'text', className: 'required', value: name
            }));
        }
        const fields = createVectorStoreResourceFields({
            value, errors: errors?.vectorStores?.[name], mxResources, onRender, managedVectorAllowed,
            onChange: next => {
                if (draft) {
                    draft.value = next;
                } else {
                    current.vectorStores[name] = next;
                    emit();
                }
            }
        });
        section.appendChild(fields.element);
        if (addButton) {
            element.insertBefore(section, addButton);
        } else {
            element.appendChild(section);
        }
    };
    Object.entries(current.vectorStores).forEach(([name, value]) => appendResource(name, value));
    drafts.forEach(draft => appendResource(null, draft.value, draft));

    if (editable) {
        addButton = document.createElement('button');
        addButton.type = 'button';
        addButton.className = 'ui mini compact button';
        addButton.textContent = mxResources.get('vectorResourceAdd');
        addButton.addEventListener('mousedown', event => event.preventDefault());
        addButton.addEventListener('click', () => {
            const value = {vectorStore: {provider: ''}, embeddingModel: {
                credentials: '', provider: '', model: '', dimensions: null
            }};
            const draft = {name: '', value};
            drafts.push(draft);
            appendResource(null, value, draft);
            onRender();
            onDraftAdded();
        });
        element.appendChild(addButton);
    } else {
        disableFormControls(element);
    }
    const validateNames = () => {
        const entries = [...nameEntries.values()];
        const proposed = entries.map(entry => entry.input.value);
        let valid = true;
        entries.forEach((entry, index) => {
            const name = proposed[index];
            const message = !name || !name.trim() ? mxResources.get('vectorResourceNameRequired')
                : name === '__proto__' ? mxResources.get('vectorResourceNameInvalid')
                    : proposed.indexOf(name) !== index ||
                      (name !== entry.getName() && Object.hasOwn(current.vectorStores, name))
                        ? mxResources.get('vectorResourceNameDuplicate') : null;
            entry.showError(message);
            valid = !message && valid;
        });
        if (valid) {
            entries.forEach(entry => entry.rename());
        }
        return valid;
    };
    return {element, getResources: transportResources, validateNames};
};

export {createVectorStoreResourcesForm, createVectorStoreResourceFields};
